<template>
  <div class="violin-container">
    <div ref="chartRef" class="chart"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'ViolinBoxPlot',
  data() {
    return {
      chart: null,

      // 固定数据
      rawData: {
        Group1: [12, 14, 13, 15, 17, 19, 18, 20, 16, 14, 15, 13, 18],
        Group2: [16, 18, 20, 21, 19, 23, 25, 22, 24, 26, 17, 18, 21]
      }
    }
  },
  mounted() {
    this.initChart()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) this.chart.dispose()
  },
  methods: {
    resizeChart() {
      if (this.chart) this.chart.resize()
    },

    initChart() {
      this.chart = echarts.init(this.$refs.chartRef)

      const groups = Object.keys(this.rawData)
      const values = Object.values(this.rawData)

      const boxData = values.map(v => this.calculateBoxplot(v))

      const option = {
        tooltip: { trigger: 'item' },
        grid: { left: 60, right: 40, top: 40, bottom: 60 },
        xAxis: {
          type: 'category',
          data: groups
        },
        yAxis: {
          type: 'value',
          name: 'Value'
        },
        series: [
          ...this.createViolinSeries(groups, values),
          {
            name: 'Boxplot',
            type: 'boxplot',
            data: boxData,
            itemStyle: {
              color: '#ffffff',
              borderColor: '#333'
            }
          },
          ...this.createScatterSeries(groups, values),
          ...this.createMeanSeries(groups, values)
        ]
      }

      this.chart.setOption(option)
    },

    // =============================
    // 箱线图计算
    // =============================
    calculateBoxplot(data) {
      const sorted = [...data].sort((a, b) => a - b)

      const q1 = this.quantile(sorted, 0.25)
      const q2 = this.quantile(sorted, 0.5)
      const q3 = this.quantile(sorted, 0.75)

      return [
        Math.min(...sorted),
        q1,
        q2,
        q3,
        Math.max(...sorted)
      ]
    },

    quantile(arr, q) {
      const pos = (arr.length - 1) * q
      const base = Math.floor(pos)
      const rest = pos - base

      if (arr[base + 1] !== undefined) {
        return arr[base] + rest * (arr[base + 1] - arr[base])
      } else {
        return arr[base]
      }
    },

    // =============================
    // KDE 小提琴
    // =============================
    kernelDensityEstimator(data, bandwidth = 1.5) {
      return y => {
        return data.reduce((sum, val) => {
          return sum + Math.exp(-0.5 * Math.pow((y - val) / bandwidth, 2))
        }, 0) / data.length
      }
    },

    createViolinSeries(groups, values) {
      const series = []

      groups.forEach((group, index) => {
        const data = values[index]
        const min = Math.min(...data)
        const max = Math.max(...data)

        const kde = this.kernelDensityEstimator(data)

        const points = []
        const steps = 60
        const step = (max - min) / steps

        for (let y = min; y <= max; y += step) {
          points.push([index, y, kde(y)])
        }

        const maxDensity = Math.max(...points.map(p => p[2]))

        series.push({
          name: group + ' Violin',
          type: 'custom',
          renderItem: (params, api) => {
            const x = api.value(0)
            const y = api.value(1)
            const density = api.value(2)

            const coord = api.coord([x, y])
            const width = (density / maxDensity) * 60

            return {
              type: 'rect',
              shape: {
                x: coord[0] - width / 2,
                y: coord[1],
                width: width,
                height: 2
              },
              style: {
                fill: 'rgba(64,158,255,0.35)'
              }
            }
          },
          data: points,
          silent: true
        })
      })

      return series
    },

    // =============================
    // 原始散点
    // =============================
    createScatterSeries(groups, values) {
      return groups.map((group, index) => ({
        name: group + ' points',
        type: 'scatter',
        data: values[index].map(val => [
          index + (Math.random() - 0.5) * 0.15,
          val
        ]),
        symbolSize: 6,
        itemStyle: {
          color: '#555'
        }
      }))
    },

    // =============================
    // 均值红点
    // =============================
    createMeanSeries(groups, values) {
      return [{
        name: 'Mean',
        type: 'scatter',
        data: groups.map((g, i) => [
          i,
          values[i].reduce((a, b) => a + b, 0) / values[i].length
        ]),
        symbolSize: 12,
        itemStyle: {
          color: 'red'
        }
      }]
    }
  }
}
</script>

<style scoped>
.violin-container {
  width: 100%;
  height: 100%;
}

.chart {
  width: 100%;
  height: 600px;
}
</style>